import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useGetAppealQuery, useMerchantRespondToAppealMutation } from "../store/api";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;

  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: #6b7280;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background: #4b5563;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
`;

const Card = styled.div`
  background: var(--bg-card);
  border-radius: 0.75rem;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const SectionTitle = styled.h2`
  margin: 0 0 1rem 0;
  font-size: 1.125rem;
  font-weight: 600;
  color: #111827;
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
`;

const InfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const Label = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-muted);
`;

const Value = styled.span`
  font-size: 0.875rem;
  color: #111827;
  font-weight: 500;
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  background: ${(props) => {
    switch (props.status) {
      case "pending":
        return "#fef3c7";
      case "resolved":
        return "#d1fae5";
      case "rejected":
        return "#fee2e2";
      case "error":
        return "#fecaca";
      default:
        return "#e5e7eb";
    }
  }};
  color: ${(props) => {
    switch (props.status) {
      case "pending":
        return "#92400e";
      case "resolved":
        return "#065f46";
      case "rejected":
        return "#991b1b";
      case "error":
        return "#dc2626";
      default:
        return "#374151";
    }
  }};
`;

const FileGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
`;

const FileItem = styled.a`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem;
  border: 1px solid var(--border-divider);
  border-radius: 0.5rem;
  text-decoration: none;
  color: var(--text-primary);
  transition: all 0.2s;

  &:hover {
    border-color: #3b82f6;
    background: #f0f9ff;
  }
`;

const FileIcon = styled.div`
  width: 2rem;
  height: 2rem;
  background: var(--bg-secondary);
  border-radius: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
`;

const FileName = styled.span`
  font-size: 0.75rem;
  text-align: center;
  word-break: break-all;
`;

const CommentBox = styled.div`
  background: var(--bg-primary);
  border-radius: 0.5rem;
  padding: 1rem;
  border: 1px solid var(--border-divider);
  white-space: pre-wrap;
  line-height: 1.5;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: var(--text-muted);
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 3rem;
  color: var(--accent-error);
`;

// Timeline styles (metro-style)
const TimelineContainer = styled.div`
  position: relative;
  padding-left: 2rem;
`;

const TimelineItem = styled.div`
  position: relative;
  padding-bottom: 1.5rem;
  
  &:last-child {
    padding-bottom: 0;
  }
  
  &::before {
    content: '';
    position: absolute;
    left: -1.5rem;
    top: 0.5rem;
    width: 12px;
    height: 12px;
    background: var(--accent-primary);
    border-radius: 50%;
    border: 2px solid #fff;
    box-shadow: 0 0 0 3px rgba(25, 75, 255, 0.2);
  }
  
  &::after {
    content: '';
    position: absolute;
    left: calc(-1.5rem + 5px);
    top: 1.5rem;
    width: 2px;
    height: calc(100% - 1rem);
    background: var(--bg-hover);
  }
  
  &:last-child::after {
    display: none;
  }
`;

const TimelineContent = styled.div`
  background: var(--bg-primary);
  border: 1px solid var(--border-divider);
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
`;

const TimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
`;

const TimelineField = styled.span`
  font-weight: 600;
  font-size: 0.875rem;
  color: #111827;
`;

const TimelineDate = styled.span`
  font-size: 0.75rem;
  color: var(--text-muted);
`;

const TimelineChange = styled.div`
  font-size: 0.8125rem;
  color: var(--text-primary);
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TimelineOldValue = styled.span`
  color: var(--accent-error);
  text-decoration: line-through;
`;

const TimelineNewValue = styled.span`
  color: #059669;
  font-weight: 500;
`;

const TimelineReason = styled.div`
  font-size: 0.75rem;
  color: var(--text-muted);
  margin-top: 0.25rem;
  font-style: italic;
`;

const NoHistoryMessage = styled.div`
  text-align: center;
  color: var(--text-muted);
  font-size: 0.875rem;
  padding: 1.5rem;
`;

function AppealDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { data: appeal, isLoading, error } = useGetAppealQuery(id!);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("ru-RU", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getStatusText = (status: string) => {
    if (!status) return "--";
    const cleanStatus = status.replace(/"/g, '').toUpperCase();
    switch (cleanStatus) {
      case "PENDING":
        return "На рассмотрении";
      case "IN_PROGRESS":
        return "В работе";
      case "APPROVED":
        return "Одобрена";
      case "REJECTED":
        return "Отклонена";
      case "RESOLVED":
        return "Решена";
      case "SUPPORT_REVIEW":
        return "На рассмотрении саппорта";
      case "MERCHANT_RESP_PENDING":
        return "Ожидает вашего ответа";
      case "TRADER_REVIEW":
        return "На повторном рассмотрении";
      case "SECONDARY_DISPUTE":
        return "Вторичная апелляция";
      case "ERROR":
        return "Ошибка";
      default:
        return status;
    }
  };

  // Форматирование значений для Timeline
  const formatLogValue = (field: string, value: string | null): string => {
    if (!value) return "";
    // Убираем кавычки
    const cleanValue = value.replace(/"/g, '');
    if (field === 'status') {
      return getStatusText(cleanValue);
    }
    return cleanValue;
  };

  const parseFileArray = (fileData: any) => {
    if (!fileData) return [];
    if (typeof fileData === "string") {
      try {
        return JSON.parse(fileData);
      } catch {
        return [];
      }
    }
    return Array.isArray(fileData) ? fileData : [];
  };

  // ===== All hooks must be called before any early returns =====
  const [timeLeft, setTimeLeft] = useState<string>("");
  const [respondComment, setRespondComment] = useState("");
  const [respondFiles, setRespondFiles] = useState<File[]>([]);
  const [respondError, setRespondError] = useState("");
  const [respondSuccess, setRespondSuccess] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [merchantRespond, { isLoading: isResponding }] = useMerchantRespondToAppealMutation();

  useEffect(() => {
    if (!appeal || appeal.status !== "MERCHANT_RESP_PENDING" || !appeal.deadline) return;
    const updateTimer = () => {
      const now = new Date().getTime();
      const deadline = new Date(appeal.deadline).getTime();
      const diff = deadline - now;
      if (diff <= 0) {
        setTimeLeft("Время истекло");
        return;
      }
      const hours = Math.floor(diff / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);
      setTimeLeft(`${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`);
    };
    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [appeal?.status, appeal?.deadline, appeal]);

  if (isLoading) {
    return <LoadingContainer>Загрузка апелляции...</LoadingContainer>;
  }

  if (error || !appeal) {
    return <ErrorContainer>Ошибка загрузки апелляции</ErrorContainer>;
  }

  const images = parseFileArray(appeal.images);
  const videos = parseFileArray(appeal.videos);
  const documents = parseFileArray(appeal.documents);
  const allFiles = [...images, ...videos, ...documents];

  const handleRespondSubmit = async () => {
    if (!respondComment.trim()) {
      setRespondError("Укажите комментарий");
      return;
    }
    if (respondFiles.length === 0) {
      setRespondError("Приложите хотя бы один файл");
      return;
    }
    setRespondError("");
    try {
      await merchantRespond({ id: appeal.id, files: respondFiles, comment: respondComment }).unwrap();
      setRespondSuccess(true);
      setRespondComment("");
      setRespondFiles([]);
    } catch (err: any) {
      setRespondError(err?.data?.message || "Ошибка при отправке ответа");
    }
  };

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate("/appeals")}>
          ← Назад к апелляциям
        </BackButton>
        <Title>Апелляция #{appeal.id.substring(0, 8)}</Title>
      </Header>

      <Card>
        <SectionTitle>Основная информация</SectionTitle>
        <InfoGrid>
          <InfoItem>
            <Label>ID Апелляции</Label>
            <Value>{appeal.id}</Value>
          </InfoItem>
          <InfoItem>
            <Label>ID Ордера</Label>
            <Value>{appeal.order?.invId || appeal.orderId}</Value>
          </InfoItem>
          <InfoItem>
            <Label>Статус</Label>
            <StatusBadge status={appeal.status}>
              {getStatusText(appeal.status)}
            </StatusBadge>
          </InfoItem>
          <InfoItem>
            <Label>Создана</Label>
            <Value>{formatDate(appeal.createdAt)}</Value>
          </InfoItem>
          <InfoItem>
            <Label>Обновлена</Label>
            <Value>
              {appeal.updatedAt ? formatDate(appeal.updatedAt) : "—"}
            </Value>
          </InfoItem>
          <InfoItem>
            <Label>Причина</Label>
            <Value>{appeal.reason}</Value>
          </InfoItem>
        </InfoGrid>
      </Card>

      {/* Timer and response form for MERCHANT_RESP_PENDING */}
      {appeal.status === "MERCHANT_RESP_PENDING" && (
        <Card style={{ border: '2px solid #f59e0b' }}>
          <SectionTitle>Требуется ваш ответ</SectionTitle>
          {appeal.deadline && (
            <div style={{ marginBottom: '1rem', padding: '0.75rem 1rem', background: timeLeft === 'Время истекло' ? '#fee2e2' : '#fef3c7', borderRadius: '0.5rem', fontWeight: 600, fontSize: '1rem', textAlign: 'center', color: timeLeft === 'Время истекло' ? '#dc2626' : '#92400e' }}>
              Оставшееся время: {timeLeft}
            </div>
          )}

          {/* Комментарий саппорта для мерчанта */}
          {(() => {
            const adminComments = (appeal.changeLogs || [])
              .filter((log: any) => log.field === 'supportComment')
              .reverse();
            if (adminComments.length === 0) return null;
            return (
              <div style={{ marginBottom: '1rem', padding: '1rem', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: '0.5rem' }}>
                <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#92400e', marginBottom: '0.5rem' }}>
                  💬 Комментарий саппорта:
                </div>
                {adminComments.map((log: any, idx: number) => (
                  <div key={log.id || idx} style={{ fontSize: '0.875rem', color: '#78350f', lineHeight: 1.5 }}>
                    {log.newValue}
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Доказательства трейдера (файлы без комментария) */}
          {(() => {
            let traderFiles: any[] = [];
            if (appeal.traderEvidence) {
              try {
                const evidence = typeof appeal.traderEvidence === "string" 
                  ? JSON.parse(appeal.traderEvidence) 
                  : appeal.traderEvidence;
                if (evidence.files && Array.isArray(evidence.files)) {
                  traderFiles = evidence.files;
                }
              } catch (e) {}
            }
            if (traderFiles.length === 0) return null;
            const base = (process.env.REACT_APP_API_URL || '').replace(/\/api\/merchant$/, '').replace(/\/api$/, '');
            return (
              <div style={{ marginBottom: '1rem', padding: '1rem', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: '0.5rem' }}>
                <div style={{ fontWeight: 600, fontSize: '0.875rem', color: '#0369a1', marginBottom: '0.75rem' }}>
                  📎 Доказательства трейдера ({traderFiles.length})
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
                  {traderFiles.map((file: any, index: number) => {
                    const fileUrl = file.url || file.path || '';
                    const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${base}${fileUrl}`;
                    return (
                      <a
                        key={index}
                        href={fullUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
                          padding: '0.375rem 0.75rem', background: 'white', border: '1px solid #93c5fd',
                          borderRadius: '0.5rem', color: '#1d4ed8', fontSize: '0.8125rem',
                          textDecoration: 'none', transition: 'all 0.2s',
                        }}
                      >
                        {file.originalName?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? "🖼️" :
                         file.originalName?.match(/\.(mp4|avi|mov|webm)$/i) ? "🎬" : "📄"}
                        {file.originalName || "Файл"}
                      </a>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          {respondSuccess ? (
            <div style={{ padding: '1rem', background: '#d1fae5', borderRadius: '0.5rem', color: '#065f46', textAlign: 'center', fontWeight: 500 }}>
              ✅ Ответ отправлен
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <Label style={{ display: 'block', marginBottom: '0.5rem' }}>Комментарий</Label>
                <textarea
                  value={respondComment}
                  onChange={(e) => setRespondComment(e.target.value)}
                  placeholder="Ваш комментарий..."
                  style={{ width: '100%', minHeight: '80px', padding: '0.75rem', border: '1px solid #d1d5db', borderRadius: '0.5rem', resize: 'vertical', fontFamily: 'inherit', fontSize: '0.875rem', boxSizing: 'border-box', outline: 'none', transition: 'border-color 0.2s' }}
                  onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                  onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
                />
              </div>
              <div>
                <Label style={{ display: 'block', marginBottom: '0.5rem' }}>Файлы (видео, выписки, скриншоты)</Label>
                <input
                  type="file"
                  ref={fileInputRef}
                  multiple
                  accept="image/*,video/*,.pdf"
                  onChange={(e) => setRespondFiles(Array.from(e.target.files || []))}
                  style={{ display: 'none' }}
                />
                <div
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    border: respondFiles.length > 0 ? '2px solid #22c55e' : '2px dashed #d1d5db',
                    borderRadius: '0.5rem',
                    padding: '1.25rem',
                    textAlign: 'center',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    background: respondFiles.length > 0 ? '#f0fdf4' : '#fafafa',
                  }}
                  onMouseOver={(e) => { if (respondFiles.length === 0) e.currentTarget.style.borderColor = '#3b82f6'; e.currentTarget.style.background = respondFiles.length > 0 ? '#f0fdf4' : '#eff6ff'; }}
                  onMouseOut={(e) => { if (respondFiles.length === 0) e.currentTarget.style.borderColor = '#d1d5db'; e.currentTarget.style.background = respondFiles.length > 0 ? '#f0fdf4' : '#fafafa'; }}
                >
                  {respondFiles.length === 0 ? (
                    <>
                      <div style={{ fontSize: '1.5rem', marginBottom: '0.25rem' }}>📎</div>
                      <div style={{ fontSize: '0.875rem', fontWeight: 500, color: '#374151' }}>Нажмите для загрузки файлов</div>
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af', marginTop: '0.25rem' }}>Изображения, видео или PDF</div>
                    </>
                  ) : (
                    <>
                      <div style={{ fontSize: '1rem', color: '#16a34a', fontWeight: 600, marginBottom: '0.5rem' }}>✓ Загружено файлов: {respondFiles.length}</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', justifyContent: 'center' }}>
                        {respondFiles.map((file, i) => (
                          <span key={i} style={{ fontSize: '0.75rem', background: 'white', border: '1px solid #bbf7d0', borderRadius: '0.25rem', padding: '0.125rem 0.5rem', color: '#15803d' }}>
                            {file.name.length > 25 ? file.name.substring(0, 22) + '...' : file.name}
                          </span>
                        ))}
                      </div>
                      <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem', cursor: 'pointer' }}>
                        Нажмите, чтобы заменить файлы
                      </div>
                    </>
                  )}
                </div>
              </div>
              {respondError && (
                <div style={{ color: '#dc2626', fontSize: '0.875rem', padding: '0.5rem', background: '#fef2f2', borderRadius: '0.375rem', border: '1px solid #fecaca' }}>{respondError}</div>
              )}
              <button
                onClick={handleRespondSubmit}
                disabled={isResponding}
                style={{
                  padding: '0.75rem 1.5rem', 
                  background: isResponding ? '#9ca3af' : '#194bff', 
                  color: 'white', border: 'none', borderRadius: '0.5rem', 
                  cursor: isResponding ? 'default' : 'pointer', 
                  fontWeight: 600, fontSize: '0.9375rem',
                  transition: 'all 0.2s',
                  boxShadow: isResponding ? 'none' : '0 2px 4px rgba(25, 75, 255, 0.25)',
                }}
                onMouseOver={(e) => { if (!isResponding) (e.target as any).style.background = '#1240e0'; }}
                onMouseOut={(e) => { if (!isResponding) (e.target as any).style.background = '#194bff'; }}
              >
                {isResponding ? '⏳ Отправка...' : '📤 Отправить ответ'}
              </button>
            </div>
          )}
        </Card>
      )}

      {appeal.order && (
        <Card>
          <SectionTitle>Детали ордера</SectionTitle>
          <InfoGrid>
            <InfoItem>
              <Label>Сумма</Label>
              <Value>
                {appeal.order.amount} {appeal.order.currency}
              </Value>
            </InfoItem>
            <InfoItem>
              <Label>Статус ордера</Label>
              <Value>{appeal.order.status}</Value>
            </InfoItem>
            <InfoItem>
              <Label>Банк</Label>
              <Value>{appeal.order.requisite?.bank?.name || appeal.order.bank || "—"}</Value>
            </InfoItem>
            <InfoItem>
              <Label>Реквизит</Label>
              <Value>
                {appeal.order.requisite ? (
                  <>
                    {appeal.order.requisite.cardNumber || appeal.order.requisite.phoneNumber || appeal.order.requisite.sbpPhoneNumber || appeal.order.requisite.accountNumber || "—"}
                    {appeal.order.requisite.bank?.name && (
                      <span style={{ color: "var(--text-muted)", marginLeft: 4, fontSize: "0.75rem" }}>
                        ({appeal.order.requisite.bank.name})
                      </span>
                    )}
                  </>
                ) : "—"}
              </Value>
            </InfoItem>
            <InfoItem>
              <Label>Тип операции</Label>
              <Value>{appeal.order.type}</Value>
            </InfoItem>
            <InfoItem>
              <Label>Создан</Label>
              <Value>{formatDate(appeal.order.createdAt)}</Value>
            </InfoItem>
            {appeal.order.completedAt && (
              <InfoItem>
                <Label>Завершен</Label>
                <Value>{formatDate(appeal.order.completedAt)}</Value>
              </InfoItem>
            )}
          </InfoGrid>
        </Card>
      )}

      {appeal.comment && (
        <Card>
          <SectionTitle>Комментарий</SectionTitle>
          <CommentBox>{appeal.comment}</CommentBox>
        </Card>
      )}

      {allFiles.length > 0 && (
        <Card>
          <SectionTitle>Прикрепленные файлы ({allFiles.length})</SectionTitle>
          <FileGrid>
          {allFiles.map((file, index) => {
              const fileUrl = (file.url || file.path || '');
              // Strip /api from API URL since static files are served from /uploads not /api/uploads
              const baseUrl = (process.env.REACT_APP_API_URL || '').replace(/\/api\/merchant$/, '').replace(/\/api$/, '');
              const fullUrl = fileUrl.startsWith('http') 
                ? fileUrl 
                : `${baseUrl}${fileUrl}`;
              return (
                <FileItem
                  key={index}
                  href={fullUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <FileIcon>
                    {file.type === "image" && "🖼"}
                    {file.type === "video" && "🎬"}
                    {file.type === "document" && "📄"}
                    {!file.type && "📎"}
                  </FileIcon>
                  <FileName>
                    {file.originalName || file.originalname || fileUrl.split("/").pop() || "Файл"}
                  </FileName>
                </FileItem>
              );
            })}
          </FileGrid>
        </Card>
      )}
      {/* Доказательства трейдера (файлы без комментария — бэкенд фильтрует) */}
      {(() => {
        let traderFiles: any[] = [];
        if (appeal.traderEvidence) {
          try {
            const evidence = typeof appeal.traderEvidence === "string" 
              ? JSON.parse(appeal.traderEvidence) 
              : appeal.traderEvidence;
            if (evidence.files && Array.isArray(evidence.files)) {
              traderFiles = evidence.files;
            }
          } catch (e) {}
        }
        
        if (traderFiles.length === 0) return null;
        const base = (process.env.REACT_APP_API_URL || '').replace(/\/api\/merchant$/, '').replace(/\/api$/, '');
        
        return (
          <Card style={{ border: '1px solid #bae6fd' }}>
            <SectionTitle>📎 Доказательства трейдера</SectionTitle>
            <FileGrid>
              {traderFiles.map((file: any, index: number) => {
                const fileUrl = file.url || file.path || '';
                const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${base}${fileUrl}`;
                return (
                  <FileItem
                    key={index}
                    href={fullUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FileIcon>
                      {file.originalName?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? "🖼️" :
                       file.originalName?.match(/\.(mp4|avi|mov|webm)$/i) ? "🎬" : "📄"}
                    </FileIcon>
                    <FileName>
                      {file.originalName || file.originalname || "Файл"}
                    </FileName>
                  </FileItem>
                );
              })}
            </FileGrid>
          </Card>
        );
      })()}

      {/* История доказательств мерчанта — таблица из changeLogs */}
      {(() => {
        const evidenceLogs = (appeal.changeLogs || [])
          .filter((log: any) => log.field === 'merchantEvidence')
          .reverse(); // хронологический порядок
        
        let currentFiles: any[] = [];
        let currentComment = "";
        if (appeal.merchantEvidence) {
          try {
            const evidence = typeof appeal.merchantEvidence === "string" 
              ? JSON.parse(appeal.merchantEvidence) 
              : appeal.merchantEvidence;
            if (evidence.files && Array.isArray(evidence.files)) currentFiles = evidence.files;
            if (evidence.comment) currentComment = evidence.comment;
          } catch (e) {}
        }
        
        const hasHistory = evidenceLogs.length > 0;
        const hasCurrent = currentFiles.length > 0 || currentComment || appeal.merchantComment;
        
        if (!hasHistory && !hasCurrent) return null;
        
        const base = (process.env.REACT_APP_API_URL || '').replace(/\/api\/merchant$/, '').replace(/\/api$/, '');
        
        const renderFiles = (files: any[]) => (
          <FileGrid>
            {files.map((file: any, index: number) => {
              const fileUrl = file.url || file.path || '';
              const fullUrl = fileUrl.startsWith('http') ? fileUrl : `${base}${fileUrl}`;
              return (
                <FileItem key={index} href={fullUrl} target="_blank" rel="noopener noreferrer">
                  <FileIcon>
                    {file.originalName?.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? "🖼" :
                     file.originalName?.match(/\.(mp4|avi|mov|webm)$/i) ? "🎬" : "📄"}
                  </FileIcon>
                  <FileName>{file.originalName || file.originalname || "Файл"}</FileName>
                </FileItem>
              );
            })}
          </FileGrid>
        );
        
        return (
          <Card>
            <SectionTitle>Мои доказательства</SectionTitle>
            {hasHistory ? (
              <TimelineContainer>
                {evidenceLogs.map((log: any, idx: number) => {
                  let files: any[] = [];
                  let comment = "";
                  try {
                    let ev = typeof log.newValue === 'string' ? JSON.parse(log.newValue) : log.newValue;
                    // Обработка двойной сериализации
                    if (typeof ev === 'string') ev = JSON.parse(ev);
                    if (ev?.files && Array.isArray(ev.files)) files = ev.files;
                    if (ev?.comment) comment = ev.comment;
                  } catch (e) {}
                  
                  return (
                    <TimelineItem key={log.id || idx}>
                      <TimelineContent>
                        <TimelineHeader>
                          <TimelineField>Доказательство #{idx + 1}</TimelineField>
                          <TimelineDate>{formatDate(log.createdAt)}</TimelineDate>
                        </TimelineHeader>
                        {files.length > 0 && renderFiles(files)}
                        {comment && (
                          <CommentBox style={{ marginTop: '0.5rem' }}>{comment}</CommentBox>
                        )}
                      </TimelineContent>
                    </TimelineItem>
                  );
                })}
              </TimelineContainer>
            ) : (
              <>
                {currentFiles.length > 0 && renderFiles(currentFiles)}
                {(currentComment || appeal.merchantComment) && (
                  <CommentBox style={{ marginTop: currentFiles.length > 0 ? "1rem" : 0 }}>
                    {currentComment || appeal.merchantComment}
                  </CommentBox>
                )}
              </>
            )}
          </Card>
        );
      })()}

      {/* Комментарий админа для мерчанта (из changeLogs supportComment) */}
      {(() => {
        const adminComments = (appeal.changeLogs || [])
          .filter((log: any) => log.field === 'supportComment')
          .reverse();
        if (adminComments.length === 0) return null;
        return (
          <Card style={{ border: '1px solid #fef3c7' }}>
            <SectionTitle>Комментарии саппорта</SectionTitle>
            <TimelineContainer>
              {adminComments.map((log: any, idx: number) => (
                <TimelineItem key={log.id || idx}>
                  <TimelineContent>
                    <TimelineHeader>
                      <TimelineField>Саппорт</TimelineField>
                      <TimelineDate>{formatDate(log.createdAt)}</TimelineDate>
                    </TimelineHeader>
                    <CommentBox style={{ marginTop: '0.25rem' }}>{log.newValue}</CommentBox>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </TimelineContainer>
          </Card>
        );
      })()}

      {/* История изменений — фильтруем traderEvidence, merchantEvidence, supportComment */}
      <Card>
        <SectionTitle>История изменений</SectionTitle>
        {appeal.changeLogs && appeal.changeLogs.length > 0 ? (
          <TimelineContainer>
            {appeal.changeLogs
              .filter((log: any) => {
                if (log.field === 'traderEvidence') return false;
                if (log.field === 'merchantEvidence') return false;
                if (log.field === 'supportComment') return false;
                return true;
              })
              .map((log: any) => { 
                const fieldLabel = log.field === 'status' ? 'Изменение статуса' :
                         log.field === 'comment' ? 'Комментарий' :
                         log.field === 'deadline' ? 'Дедлайн' :
                         log.field;

                return (
                <TimelineItem key={log.id}>
                  <TimelineContent>
                    <TimelineHeader>
                      <TimelineField>{fieldLabel}</TimelineField>
                      <TimelineDate>{formatDate(log.createdAt)}</TimelineDate>
                    </TimelineHeader>
                    <TimelineChange>
                      {log.oldValue && (
                        <TimelineOldValue>{formatLogValue(log.field, log.oldValue)}</TimelineOldValue>
                      )}
                      {log.oldValue && log.newValue && <span>→</span>}
                      {log.newValue && (
                        <TimelineNewValue>{formatLogValue(log.field, log.newValue)}</TimelineNewValue>
                      )}
                    </TimelineChange>
                    {log.reason && (
                      <TimelineReason>{log.reason}</TimelineReason>
                    )}
                  </TimelineContent>
                </TimelineItem>
                );
              })}
            
            {/* Создание апелляции — в конце */}
            <TimelineItem>
              <TimelineContent>
                <TimelineHeader>
                  <TimelineField>Апелляция создана</TimelineField>
                  <TimelineDate>{formatDate(appeal.createdAt)}</TimelineDate>
                </TimelineHeader>
                <TimelineChange>
                  <TimelineNewValue>Статус: На рассмотрении</TimelineNewValue>
                </TimelineChange>
                {appeal.reason && (
                  <TimelineReason>Причина: {appeal.reason}</TimelineReason>
                )}
              </TimelineContent>
            </TimelineItem>
          </TimelineContainer>
        ) : (
          <TimelineContainer>
            <TimelineItem>
              <TimelineContent>
                <TimelineHeader>
                  <TimelineField>Апелляция создана</TimelineField>
                  <TimelineDate>{formatDate(appeal.createdAt)}</TimelineDate>
                </TimelineHeader>
                <TimelineChange>
                  <TimelineNewValue>Статус: {getStatusText(appeal.status)}</TimelineNewValue>
                </TimelineChange>
                {appeal.reason && (
                  <TimelineReason>Причина: {appeal.reason}</TimelineReason>
                )}
              </TimelineContent>
            </TimelineItem>
          </TimelineContainer>
        )}
      </Card>
    </Container>
  );
}

export default AppealDetail;
